<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="sixty" size="title" />
                <h2 class="title-subsection text-valence-stone-900">360s</h2>
                <TabInfoToggle v-if="hasAnyItem" :expanded="infoOpen" topic="360s" @toggle="infoOpen = !infoOpen" />
                <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
                <a
                    v-if="hasAnyItem"
                    href="/reflections/360/start"
                    class="base-btn-dark base-btn-sm ml-auto inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap"
                    @click.prevent="emit('new-360')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    New 360
                </a>
            </div>
            <p class="body-small text-valence-stone-500">
                A 360 gathers anonymous feedback from the people you work with — the outside view that grounds your review and coaching.
            </p>
        </header>

        <hr class="border-t border-valence-cream-300" />

        <HowItWorks360 v-if="!hasAnyItem" @start="emit('new-360')" />

        <BaseDialog v-model:open="infoOpen" title="How 360s work" show-close float-close dialog-content-class="md:!max-w-2xl">
            <HowItWorks360 @start="onStartFromInfo" />
        </BaseDialog>

        <div v-if="activeCycles.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-valence-stone-500">Active 360s</div>
            <Active360Card
                v-for="cycle in activeCycles"
                :key="cycle.id"
                :cycle="cycle"
                @invite-more="onInviteMore"
                @remind="onRemind"
                @wrap-up="onWrapUp"
                @open-self="emit('open-self', $event)"
                @deleted="emit('deleted', $event)" />
        </div>

        <div v-if="pendingTasks.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-valence-stone-500">Ready to start</div>
            <SelfTaskRow v-for="task in pendingTasks" :key="task.id" :task="task" :show-icon="false" @start="onStartTask" />
        </div>

        <div v-if="pastCycles.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-valence-stone-500">Past 360s</div>
            <PastCycleCard v-for="cycle in pastCycles" :key="cycle.id" :cycle="cycle" kind="sixty" @view="emit('view-cycle', $event)" />
        </div>

        <AssessmentDemoTools :cycle-actions="cycleDemoActions" bootstrap-kind="sixty" @deleted="emit('deleted', $event)" />
    </section>
</template>

<script setup lang="ts">
import type { CycleForSubject, PastCycleSummary, PendingPersonalTask } from "~vue/api/generated/reflections";
import BaseDialog from "~vue/components/BaseDialog.vue";
import Active360Card from "~vue/pages/reflections/Active360Card.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import HowItWorks360 from "~vue/pages/reflections/HowItWorks360.vue";
import PastCycleCard from "~vue/pages/reflections/PastCycleCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfTaskRow from "~vue/pages/reflections/SelfTaskRow.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import { computed, ref } from "vue";

interface Props {
    activeCycles: CycleForSubject[];
    pastCycles: PastCycleSummary[];
    // 360-flavored personal tasks: own 360 cycles in DRAFT (setup not done) and
    // org campaigns the user hasn't started their cycle from yet. Filtering to
    // `!is_self_assessment_only` happens at the caller.
    pendingTasks?: PendingPersonalTask[];
}

const props = withDefaults(defineProps<Props>(), { pendingTasks: () => [] });
const emit = defineEmits<{
    (event: "invite-more", cycleId: string): void;
    (event: "remind", inviteId: string): void;
    (event: "wrap-up", cycleId: string): void;
    (event: "view-cycle", cycleId: string): void;
    (event: "nominate", cycleId: string): void;
    (event: "start-from-campaign", payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }): void;
    (event: "open-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "new-360"): void;
    (event: "deleted", cycleId: string): void;
}>();

function onStartTask(task: PendingPersonalTask) {
    if (task.kind === "cycle_setup") {
        emit("nominate", task.id);
        return;
    }
    if (task.kind === "cycle_setup_campaign") {
        emit("start-from-campaign", { campaignId: task.id, title: task.title, isSelfAssessmentOnly: false });
    }
}

const hasAnyItem = computed(() => props.activeCycles.length > 0 || props.pastCycles.length > 0 || props.pendingTasks.length > 0);
// Every 360 the user owns is listed so it can be deleted; only the ones the
// synthetic-fill endpoint accepts (draft or collecting, with someone left to
// answer) offer a fill.
const cycleDemoActions = computed(() => {
    const active = props.activeCycles.map((cycle) => ({
        id: cycle.id,
        label: cycle.template.name,
        canFill: (cycle.state === "draft" || cycle.state === "collecting") && (cycle.is_self_assessment_only || cycle.response_summary.pending > 0),
    }));
    const setupTasks = props.pendingTasks.filter((task) => task.kind === "cycle_setup").map((task) => ({ id: task.id, label: task.title, canFill: false }));
    const past = props.pastCycles.map((cycle) => ({ id: cycle.id, label: cycle.template.name, canFill: false }));
    // A draft 360 shows up as both an active cycle and a setup task.
    return [...new Map([...active, ...setupTasks, ...past].map((action) => [action.id, action])).values()];
});
const infoOpen = ref(false);

function onStartFromInfo() {
    infoOpen.value = false;
    emit("new-360");
}

function onInviteMore(cycleId: string) {
    emit("invite-more", cycleId);
}
function onRemind(inviteId: string) {
    emit("remind", inviteId);
}
function onWrapUp(cycleId: string) {
    emit("wrap-up", cycleId);
}
</script>
